import type { Pos, FuelGrid, DifficultyTier, Puzzle } from "../types.ts";
import { createGrid, getFuel, setFuel, getNeighbors, totalFuel, posKey } from "./grid.ts";
import { mulberry32, randInt, randPick } from "./prng.ts";

export const DIFFICULTY_TIERS: Record<string, DifficultyTier> = {
  easy:   { name: "Easy",   rows: 4, cols: 4, minFuel: 1, maxFuel: 2 },
  medium: { name: "Medium", rows: 5, cols: 5, minFuel: 1, maxFuel: 3 },
  hard:   { name: "Hard",   rows: 6, cols: 6, minFuel: 1, maxFuel: 3 },
  expert: { name: "Expert", rows: 7, cols: 7, minFuel: 1, maxFuel: 4 },
};

/**
 * Generate a puzzle via backwards walk.
 * The walk itself IS a valid solution, so the grid is guaranteed solvable
 * from the walk's starting position. No expensive solver call needed.
 */
export function generatePuzzle(seed: number, tier: DifficultyTier): Puzzle {
  const rng = mulberry32(seed);
  const maxAttempts = 100;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const result = generateByBackwardsWalk(rng, tier);
    if (result) return result;
  }

  return createFallbackPuzzle(tier);
}

function generateByBackwardsWalk(
  rng: () => number,
  tier: DifficultyTier
): Puzzle | null {
  const { rows, cols, minFuel, maxFuel } = tier;
  const grid = createGrid(rows, cols, 0);

  // The walk traces a valid solution path.
  // Start position of walk = player's starting position in the puzzle.
  const startPos: Pos = {
    row: randInt(rng, 0, rows - 1),
    col: randInt(rng, 0, cols - 1),
  };

  let pos = startPos;
  setFuel(grid, pos, 1);
  const visited = new Set<string>();
  visited.add(posKey(pos));

  let steps = 1;
  const totalCells = rows * cols;
  const targetTotalFuel = totalCells * Math.ceil((minFuel + maxFuel) / 2);
  const maxWalkSteps = targetTotalFuel + totalCells;

  for (let i = 0; i < maxWalkSteps; i++) {
    const neighbors = getNeighbors(grid, pos);

    // Bias toward unvisited cells for full coverage
    const unvisited = neighbors.filter((n) => !visited.has(posKey(n)));
    const underMax = neighbors.filter((n) => getFuel(grid, n) < maxFuel);

    let next: Pos;
    if (unvisited.length > 0 && rng() < 0.75) {
      next = randPick(rng, unvisited);
    } else if (underMax.length > 0) {
      next = randPick(rng, underMax);
    } else {
      break;
    }

    setFuel(grid, next, getFuel(grid, next) + 1);
    visited.add(posKey(next));
    pos = next;
    steps++;
  }

  // Validate: all cells must have fuel in [minFuel, maxFuel]
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const f = getFuel(grid, { row: r, col: c });
      if (f < minFuel || f > maxFuel) return null;
    }
  }

  return {
    id: "",
    grid,
    difficulty: tier,
    totalFuel: totalFuel(grid),
    optimalSteps: steps,
    optimalStart: startPos,
  };
}

function createFallbackPuzzle(tier: DifficultyTier): Puzzle {
  // All-ones grid with a known Hamiltonian path start
  const grid = createGrid(tier.rows, tier.cols, 1);
  const tf = totalFuel(grid);
  return {
    id: "",
    grid,
    difficulty: tier,
    totalFuel: tf,
    optimalSteps: tf,
    optimalStart: { row: 0, col: 0 },
  };
}
